<script lang="ts" setup>
import { ref, watch, onMounted } from 'vue';
import 'vue3-timepicker/dist/VueTimepicker.css';
import { ElTimePicker } from 'element-plus';

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { combineDateAndTime } from '@/shared/sticker-date-combos';
import { timezones } from '@/data/timezones';

dayjs.extend(utc);
dayjs.extend(timezone);

interface Props {
  modelValue?: {
    startDate?: string;
    endDate?: string;
    startTimezone?: string;
    endTimezone?: string;
    color?: string;
  };
}

const props = defineProps<Props>();

const emit = defineEmits(['update:modelValue']);

const color = ref('#F8E587');
const dateStart = ref();
const dateEnd = ref();
const timeStart = ref();
const selectedTimezone = ref();
const timeEnd = ref();

// Helper function to normalize color format for HTML5 color input
const normalizeColor = (colorValue: string): string => {
  if (!colorValue) return '#F8E587';

  // Remove any whitespace
  let normalized = colorValue.trim();

  // Add # prefix if missing
  if (!normalized.startsWith('#')) {
    normalized = `#${normalized}`;
  }

  // Ensure it's uppercase for consistency (HTML5 color input accepts both)
  return normalized.toUpperCase();
};

onMounted(() => {
  if (props.modelValue) {
    if (props.modelValue.color) {
      color.value = normalizeColor(props.modelValue.color);
    }

    if (props.modelValue.startTimezone) {
      selectedTimezone.value = props.modelValue.startTimezone;
    }

    if (props.modelValue.startDate && props.modelValue.startTimezone) {
      const start = dayjs
        .utc(props.modelValue.startDate)
        .tz(props.modelValue.startTimezone);

      if (start && start.isValid()) {
        dateStart.value = start.format('YYYY-MM-DD');
        timeStart.value = start.format('hh:mm A');
      }
    }

    if (props.modelValue.endDate && props.modelValue.endTimezone) {
      const end = dayjs
        .utc(props.modelValue.endDate)
        .tz(props.modelValue.endTimezone);

      if (end && end.isValid()) {
        dateEnd.value = end.format('YYYY-MM-DD');
        timeEnd.value = end.format('hh:mm A');
      }
    }
  }
});

const combineGlow = () => {
  // Always emit timezone and color, even if dates aren't complete
  // This allows parent validation to detect which specific field is missing
  let startDateInUTC = '';
  let endDateInUTC = '';

  // Only compute dates if all required fields for date conversion are present
  const allFieldsPresent = dateStart.value && timeStart.value
    && dateEnd.value && timeEnd.value && selectedTimezone.value;

  if (allFieldsPresent) {
    const startDate = combineDateAndTime(dateStart.value, timeStart.value);
    const endDate = combineDateAndTime(dateEnd.value, timeEnd.value);

    const convertToUTC = (date, timeZone) => {
      const localDate = dayjs.tz(date, timeZone);
      return localDate.utc().format();
    };

    startDateInUTC = convertToUTC(startDate, selectedTimezone.value);
    endDateInUTC = convertToUTC(endDate, selectedTimezone.value);
  }

  const glowData = {
    startDate: startDateInUTC,
    endDate: endDateInUTC,
    startTimezone: selectedTimezone.value || '',
    endTimezone: selectedTimezone.value || '',
    color: color.value,
  };

  emit('update:modelValue', glowData);
};

watch([dateStart, timeStart, dateEnd, timeEnd, color, selectedTimezone], () => {
  combineGlow();
});

// Watch for external prop changes
watch(() => props.modelValue, (newValue) => {
  if (newValue) {
    if (newValue.color && newValue.color !== color.value) {
      color.value = normalizeColor(newValue.color);
    }
    if (newValue.startTimezone && newValue.startTimezone !== selectedTimezone.value) {
      selectedTimezone.value = newValue.startTimezone;
    }

    // Update dates when they change
    if (newValue.startDate && newValue.startTimezone) {
      const start = dayjs
        .utc(newValue.startDate)
        .tz(newValue.startTimezone);

      if (start && start.isValid()) {
        const newDateStart = start.format('YYYY-MM-DD');
        const newTimeStart = start.format('hh:mm A');
        if (dateStart.value !== newDateStart) {
          dateStart.value = newDateStart;
        }
        if (timeStart.value !== newTimeStart) {
          timeStart.value = newTimeStart;
        }
      }
    }

    if (newValue.endDate && newValue.endTimezone) {
      const end = dayjs
        .utc(newValue.endDate)
        .tz(newValue.endTimezone);

      if (end && end.isValid()) {
        const newDateEnd = end.format('YYYY-MM-DD');
        const newTimeEnd = end.format('hh:mm A');
        if (dateEnd.value !== newDateEnd) {
          dateEnd.value = newDateEnd;
        }
        if (timeEnd.value !== newTimeEnd) {
          timeEnd.value = newTimeEnd;
        }
      }
    }
  }
}, { deep: true });
</script>

<template lang="pug">
.c-glow-picker
  .c-glow-picker__check
    input.c-glow-picker__color(type="color" v-model="color")
    span Glow Color

  .c-glow-picker__label Glow Start Date & Time
  .c-glow-picker__group.c-glow-picker__group--middle
    .c-glow-picker__group__inner
      el-date-picker.c-glow-picker__input(v-model="dateStart" format="MM/DD/YY")
      el-time-picker.c-custom-time(
        arrow-control
        format="hh:mm A"
        v-model="timeStart"
        value-format="hh:mm A"
      )

  .c-glow-picker__label Glow End Date & Time
  .c-glow-picker__group.c-glow-picker__group--middle
    .c-glow-picker__group__inner
      el-date-picker.c-glow-picker__input(v-model="dateEnd" format="MM/DD/YY")
      el-time-picker.c-custom-time(
        v-model="timeEnd"
        arrow-control
        format="hh:mm A"
        value-format="hh:mm A"
      )

  .c-glow-picker__label Timezone
  .c-glow-picker__group
    el-select.c-glow-picker__input(v-model="selectedTimezone" placeholder="Select Timezone")
      el-option(
        v-for="tz in timezones"
        :key="tz.value"
        :label="tz.label"
        :value="tz.value"
      )
</template>

<style lang="scss" scoped>
$iHeight: 56px;

.c-glow-picker {
  font-family: "Red Hat Display";
  font-weight: 500;

  &__label {
    font-size: 14px;
    font-weight: 500;
    color: #000000;
    font-family: "Red Hat Display";
    margin-bottom: 8px;
  }

  :deep(.vue__time-picker .dropdown ul li:not([disabled]).active),
  :deep(.vue__time-picker .dropdown ul li:not([disabled]).active:focus),
  :deep(.vue__time-picker .dropdown ul li:not([disabled]).active:hover) {
    background: #1EBEAF;
  }

  &__group {
    display: flex;
    flex-direction: column;
    gap: 20px;
    margin-bottom: 20px;

    &__inner {
      display: flex;
      flex-direction: row;
      gap: 20px;
    }

    &--middle {
      margin-bottom: 26px;
    }

    :deep(.el-input__wrapper) {
      box-shadow: none;
      border: 1px solid var(--el-border-color);

      &:hover { border-color: var(--el-border-color-hover); }
      &.is-focus { border-color: var(--el-color-primary); }
    }

    :deep(.el-input--prefix .el-input__inner) {
      padding-left: 40px;
      color: #000000;
      font-family: "Red Hat Display";
      font-size: 14px;
      font-weight: 500;
    }
  }

  &__icon {
    font-size: 16px;
    margin-right: 8px;
  }

  :deep(.el-select) {
    width: 100%;
    max-width: 340px;

    .el-select__placeholder {
      color: #000000;
      font-family: "Red Hat Display";
      font-size: 14px;
      font-weight: 500;
    }

    .el-select__wrapper {
      height: 100%;
      border: none;
      box-shadow: none;
      width: 338px;
    }
  }

  &__input {
    width: 100%;
    max-width: 160px;
    height: $iHeight;
    border: 1px solid var(--el-border-color);
    border-radius: 5px;

    &.el-select:hover { border-color: var(--el-border-color-hover); }
    &.el-select:has(.el-select__wrapper.is-focused) { border-color: var(--el-color-primary); }

    &--clock-icon {
      position: absolute;
      z-index: 1;
      top: 21px;
      left: 24px;
    }

    &--time {
      input.vue__time-picker-input {
        height: 100%;
        width: 100%;
        border: none;
        border-radius: 5px;
        padding-left: 52px;
        color: #000000;
        font-family: "Red Hat Display";
        font-size: 14px;
        font-weight: 500;
      }
    }

    &.el-date-editor.el-input {
      height: $iHeight;
      box-shadow: none;
      border: none;
    }
  }

  &__check {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 22px;
  }

  &__color {
    border: 1px solid #CCCCCC;
    width: 40px;
    height: 40px;
    padding: 0;
    appearance: none;
    background-color: var(--color);
    border-radius: 100%;
    cursor: pointer;
  }
}

input[type="color"]::-webkit-color-swatch {
  border: none;
  border-radius: 50%;
}

input[type="color"]::-moz-color-swatch {
  border: none;
  border-radius: 50%;
}

input[type="color"]::-ms-color-swatch {
  border: none;
  border-radius: 50%;
}

input[type="color"]::-ms-fill {
  border: none;
  border-radius: 50%;
}

:deep(.el-date-editor.c-custom-time.el-input) {
  border-radius: 5px;
  color: #000000;
  font-family: "Red Hat Display", sans-serif;
  font-size: 14px;
  font-weight: 500;
  width: 160px;
  padding: 0;
  height: 56px;
  box-shadow: none;

  .el-input__wrapper {
    box-shadow: none;
  }

  &:focus {
    outline: none;
  }
}
</style>
